import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  Compass, 
  ShipWheel, 
  TrendingUp, 
  Handshake,
  CheckCircle2
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } }
};

const services = [
  {
    icon: Compass,
    title: "Strategic Advisory",
    subtitle: "Executive-Level Consulting",
    description: "We provide senior leadership with strategic guidance on growth initiatives, market positioning, and organizational development. Our advisory services focus on building sustainable competitive advantages.",
    features: [
      "Growth strategy development",
      "Market analysis and positioning",
      "Leadership development programs",
      "Organizational design consulting",
      "Strategic planning facilitation"
    ]
  },
  {
    icon: ShipWheel,
    title: "Operational Oversight",
    subtitle: "Hands-On Management Support",
    description: "We work directly with organizations to improve internal structures, enhance accountability, and optimize operational performance. Our hands-on approach ensures lasting change.",
    features: [
      "Process optimization",
      "Performance management systems",
      "Accountability frameworks",
      "Resource allocation strategies",
      "Operational efficiency audits"
    ]
  },
  {
    icon: TrendingUp,
    title: "Business Transformation",
    subtitle: "Restructuring for Success",
    description: "When businesses need fundamental change, we help them restructure, streamline operations, and reposition for sustainable success in evolving markets.",
    features: [
      "Business model redesign",
      "Cost structure optimization",
      "Change management leadership",
      "Turnaround strategy execution",
      "Digital transformation guidance"
    ]
  },
  {
    icon: Handshake,
    title: "Investment & Partnership",
    subtitle: "Strategic Capital Deployment",
    description: "For select businesses that align with our vision, we provide investment capital alongside strategic guidance. Our investments are designed to accelerate growth and create long-term value.",
    features: [
      "Growth capital investment",
      "Restructuring financing",
      "Launch funding support",
      "Active partnership approach",
      "Value creation planning"
    ]
  }
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="pt-32 pb-24 md:pt-40 md:pb-32 px-6 md:px-12 lg:px-24 bg-navy-950">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-4xl"
        >
          <motion.p 
            className="text-teal-500 text-xs uppercase tracking-[0.3em] font-semibold mb-4"
            variants={fadeUp}
          >
            Our Services
          </motion.p>
          <motion.h1 
            className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-8"
            variants={fadeUp}
          >
            Comprehensive Business Solutions
          </motion.h1>
          <motion.p 
            className="text-slate-400 text-lg md:text-xl leading-relaxed max-w-2xl"
            variants={fadeUp}
          >
            From strategic advisory to investment partnerships, we provide the 
            expertise and resources businesses need to transform, grow, and succeed.
          </motion.p>
        </motion.div>
      </section>

      {/* Services Grid */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="services-grid">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="space-y-16"
        >
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              variants={fadeUp}
              className={`grid lg:grid-cols-2 gap-12 items-start ${
                index % 2 === 1 ? "lg:flex-row-reverse" : ""
              }`}
              data-testid={`service-detail-${index}`}
            >
              <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                <div className="mb-6">
                  <service.icon className="w-12 h-12 text-teal-500" strokeWidth={1.5} />
                </div>
                <p className="text-teal-600 text-xs uppercase tracking-[0.2em] font-semibold mb-2">
                  {service.subtitle}
                </p>
                <h2 className="font-playfair text-3xl md:text-4xl font-semibold text-navy-950 mb-6">
                  {service.title}
                </h2>
                <p className="text-slate-500 text-lg leading-relaxed">
                  {service.description}
                </p>
              </div>

              <div className={`bg-slate-50 p-8 md:p-10 ${index % 2 === 1 ? "lg:order-1" : ""}`}>
                <h3 className="text-sm uppercase tracking-widest text-navy-950 font-semibold mb-6">
                  Key Capabilities
                </h3>
                <ul className="space-y-4">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <span className="text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Process Overview */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-slate-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.p 
            className="text-teal-600 text-xs uppercase tracking-[0.3em] font-semibold mb-4"
            variants={fadeUp}
          >
            How We Work
          </motion.p>
          <motion.h2 
            className="font-playfair text-3xl md:text-4xl font-semibold text-navy-950 mb-6 max-w-2xl"
            variants={fadeUp}
          >
            A Partnership Approach to Every Engagement
          </motion.h2>
          <motion.p 
            className="text-slate-500 text-lg leading-relaxed max-w-2xl mb-16"
            variants={fadeUp}
          >
            Every service we provide follows our core methodology: understand deeply, 
            plan strategically, execute decisively, and measure results continuously.
          </motion.p>

          <motion.div 
            className="grid md:grid-cols-4 gap-6"
            variants={staggerContainer}
          >
            {[
              { step: "01", title: "Discovery", desc: "Deep assessment of your current state and goals" },
              { step: "02", title: "Strategy", desc: "Custom roadmap for achieving measurable outcomes" },
              { step: "03", title: "Execution", desc: "Hands-on implementation alongside your team" },
              { step: "04", title: "Optimization", desc: "Continuous improvement and long-term support" }
            ].map((phase, index) => (
              <motion.div
                key={phase.step}
                variants={fadeUp}
                className="text-center"
              >
                <span className="text-teal-500 text-4xl font-playfair font-bold mb-4 block">
                  {phase.step}
                </span>
                <h3 className="font-playfair text-xl font-semibold text-navy-950 mb-2">
                  {phase.title}
                </h3>
                <p className="text-slate-500 text-sm">
                  {phase.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-navy-950">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center max-w-3xl mx-auto"
        >
          <motion.h2 
            className="font-playfair text-3xl md:text-4xl font-semibold text-white mb-6"
            variants={fadeUp}
          >
            Let's Discuss Your Needs
          </motion.h2>
          <motion.p 
            className="text-slate-400 text-lg leading-relaxed mb-10"
            variants={fadeUp}
          >
            Every business is unique. Contact us to explore which services align 
            best with your current challenges and growth objectives.
          </motion.p>
          <motion.div className="flex flex-col sm:flex-row gap-4 justify-center" variants={fadeUp}>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-teal-500 text-white px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-teal-600 transition-colors"
              data-testid="services-cta"
            >
              Start a Conversation
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/approach"
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/30 text-white px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-white/10 transition-colors"
              data-testid="approach-link"
            >
              Our Methodology
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}
